import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { memo, useState, useEffect } from "react";
import { Space, Stack, Table as MantineTable, Grid, } from "@mantine/core";
import { isEmpty } from "lodash";
import CustomPaginationWrapper from "./CustomPaginationWrapper";
import { FuzzyFilterSearchBar, } from "./FuzzyFilterSearchBar";
import SortOptions, { DEFAULT_SORT_CHOICE, } from "./SortOptions";
import { useFuzzySearchTerms } from "./context";
const Table = ({ tableName, data, sortOptions, sortChoice = DEFAULT_SORT_CHOICE, onSortChange, showFuzzySearch, fuzzySearchPrompt = "Search", fuzzyKeys, loading, uniqueEmptyMessage, tableTitle, titleSpacing = "xl", loadingComponent, emptyComponent, tableHeaderRow, tableRow, padding, itemsPerPage = 10, page, totalItems, fullData = true, pageChangeHandler, onFilterDataChange, onSearchTermsChange, ...rest }) => {
    const { state: { tableSearchTerms, timestamp: newSearchTermTimestamp }, dispatch, } = useFuzzySearchTerms();
    const [activePage, setPage] = useState(1);
    const [lowerBound, setLowerBound] = useState(1);
    const [upperBound, setUpperBound] = useState(1);
    const [filteredData, setFilteredData] = useState(data);
    useEffect(() => {
        var _a;
        const searchTerms = (_a = tableSearchTerms[tableName]) !== null && _a !== void 0 ? _a : {};
        if (isEmpty(searchTerms)) {
            setFilteredData(data);
        }
        else {
            // can have overlapping fuzzy-matches
            const uniqueMatches = {};
            Object.values(searchTerms).forEach((matchedEntityIds) => {
                for (const matchingEntityId of matchedEntityIds) {
                    if (!uniqueMatches[matchingEntityId]) {
                        uniqueMatches[matchingEntityId] = 0;
                    }
                    uniqueMatches[matchingEntityId] += 1;
                }
            });
            // use deduplicated data in table-rows
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            const requiredMatchCount = Object.keys(searchTerms).length;
            const newFilteredData = {};
            for (const key of Object.keys(uniqueMatches)) {
                if (uniqueMatches[key] === requiredMatchCount) {
                    newFilteredData[key] = data[key];
                }
            }
            setFilteredData(newFilteredData);
            // TODO: Apply sort filters -
        }
    }, [data, newSearchTermTimestamp]);
    useEffect(() => {
        onFilterDataChange === null || onFilterDataChange === void 0 ? void 0 : onFilterDataChange(filteredData);
    }, [filteredData]);
    useEffect(() => {
        return () => {
            // cleanup search terms on dismount! necessary for modals on same page, which reuse table,
            // but have different base data - like AudienceMemberTokensTable
            dispatch({ type: "REMOVE_ALL", payload: { tableName } });
        };
    }, []);
    const SortOptionsButton = () => {
        return (_jsx(SortOptions, { selected: sortChoice, options: sortOptions, apply: onSortChange, reset: () => onSortChange && onSortChange() }));
    };
    const normalizedData = Object.values(filteredData);
    const hasSortOptions = !isEmpty(sortOptions);
    return (_jsxs(_Fragment, { children: [tableTitle, tableTitle && _jsx(Space, { h: titleSpacing }), loading && loadingComponent, !loading &&
                uniqueEmptyMessage &&
                data.length === 0 && { emptyComponent }, !loading && (_jsxs(Stack, { spacing: "md", children: [showFuzzySearch && (_jsx(_Fragment, { children: _jsx(FuzzyFilterSearchBar, { tableName: tableName, data: data, fuzzyKeys: fuzzyKeys !== null && fuzzyKeys !== void 0 ? fuzzyKeys : [], prompt: fuzzySearchPrompt, sortButton: hasSortOptions && _jsx(SortOptionsButton, {}), onSearchTermsChange: onSearchTermsChange }) })), !showFuzzySearch && hasSortOptions && (_jsx(Grid, { justify: "flex-end", children: _jsx(Grid.Col, { span: "content", children: _jsx(SortOptionsButton, {}) }) })), _jsx(CustomPaginationWrapper, { activePage: page || activePage, setPage: pageChangeHandler !== null && pageChangeHandler !== void 0 ? pageChangeHandler : setPage, setLowerBound: setLowerBound, setUpperBound: setUpperBound, maxLength: totalItems || Object.keys(filteredData).length, itemsPerPage: itemsPerPage, padding: padding, children: _jsxs(MantineTable, { ...rest, children: [_jsx("thead", { children: tableHeaderRow }), _jsx("tbody", { children: normalizedData
                                        .slice(fullData ? lowerBound - 1 : 0, fullData
                                        ? upperBound
                                        : Object.values(filteredData).length + 1)
                                        /* eslint-disable  @typescript-eslint/no-explicit-any */
                                        .map((rowData, index) => tableRow(index.toString(), rowData)) })] }) })] }))] }));
};
export default memo(Table);
